import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

const EMPTY_SETTINGS = {
  systemSettings: null,
  companyConfig: null,
  enums: null,
  roles: [],
  departments: [],
  severityColors: {},
  statusColors: {},
  priorityColors: {},
  loading: true,
  error: null
};

const DEFAULT_LEVEL_COLOR = { main: '#757575', light: '#9e9e9e', dark: '#424242', contrastText: '#ffffff' };

export const SettingsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState(EMPTY_SETTINGS);

  // Load everything in parallel. Individual failures (typically a 403 for a
  // user without company:read) leave that piece null so components can show
  // an "unavailable" state instead of spinning forever.
  const loadSettings = useCallback(async () => {
    if (authLoading || !user) return;

    try {
      setSettings(prev => ({ ...prev, loading: true, error: null }));

      const [
        systemSettings,
        companyConfig,
        enums,
        roles,
        departments,
        severityColors,
        statusColors,
        priorityColors
      ] = await Promise.all([
        settingsService.getSystemSettings().catch(() => null),
        settingsService.getCompanyConfiguration().catch(() => null),
        settingsService.getConfigurableEnums().catch(() => null),
        settingsService.getRoles().catch(() => []),
        settingsService.getDepartments().catch(() => []),
        settingsService.getSeverityColors().catch(() => ({})),
        settingsService.getStatusColors().catch(() => ({})),
        settingsService.getPriorityColors().catch(() => ({}))
      ]);

      setSettings({
        systemSettings,
        companyConfig,
        enums,
        roles,
        departments,
        severityColors,
        statusColors,
        priorityColors,
        loading: false,
        error: null
      });
    } catch (error) {
      setSettings(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load settings'
      }));
    }
  }, [authLoading, user]);

  const updateSystemSettings = useCallback(async (newSettings) => {
    const result = await settingsService.updateSystemSettings(newSettings);
    setSettings(prev => ({ ...prev, systemSettings: result.settings }));
    return result;
  }, []);

  const updateCompanyConfiguration = useCallback(async (newConfig) => {
    const result = await settingsService.updateCompanyConfiguration(newConfig);
    const [roles, departments] = await Promise.all([
      settingsService.getRoles().catch(() => null),
      settingsService.getDepartments().catch(() => null)
    ]);
    setSettings(prev => ({
      ...prev,
      companyConfig: result.configuration,
      roles: roles || prev.roles,
      departments: departments || prev.departments
    }));
    return result;
  }, []);

  const updateConfigurableEnums = useCallback(async (newEnums) => {
    const result = await settingsService.updateConfigurableEnums(newEnums);
    const [severityColors, statusColors, priorityColors] = await Promise.all([
      settingsService.getSeverityColors(),
      settingsService.getStatusColors(),
      settingsService.getPriorityColors()
    ]);
    setSettings(prev => ({
      ...prev,
      enums: result.enums,
      severityColors,
      statusColors,
      priorityColors
    }));
    return result;
  }, []);

  const refreshSettings = useCallback(async () => {
    settingsService.clearAllCache();
    await loadSettings();
  }, [loadSettings]);

  const getFormattedRoles = useCallback(() => (
    settings.roles.map(role => ({ value: role.name, label: role.displayName || role.name }))
  ), [settings.roles]);

  const getFormattedDepartments = useCallback(() => (
    settings.departments.map(dept => ({ value: dept.name, label: dept.displayName || dept.name }))
  ), [settings.departments]);

  const getFormattedSeverities = useCallback(() => {
    if (!settings.enums?.severityLevels) return [];
    return settings.enums.severityLevels
      .filter(severity => severity.enabled)
      .sort((a, b) => a.order - b.order)
      .map(severity => ({ value: severity.value, label: severity.label, color: severity.color }));
  }, [settings.enums]);

  const getFormattedStatuses = useCallback((category = 'detection') => {
    if (!settings.enums?.statusOptions?.[category]) return [];
    return settings.enums.statusOptions[category]
      .filter(status => status.enabled)
      .sort((a, b) => a.order - b.order)
      .map(status => ({ value: status.value, label: status.label, color: status.color }));
  }, [settings.enums]);

  const getSeverityColor = useCallback((severity) => (
    settings.severityColors[severity] || DEFAULT_LEVEL_COLOR
  ), [settings.severityColors]);

  const getStatusColor = useCallback((status) => (
    settings.statusColors[status] || '#757575'
  ), [settings.statusColors]);

  const getPriorityColor = useCallback((priority) => (
    settings.priorityColors[priority] || DEFAULT_LEVEL_COLOR
  ), [settings.priorityColors]);

  useEffect(() => {
    if (authLoading || !user) return;
    loadSettings();
  }, [loadSettings, user, authLoading]);

  // Unauthenticated: provide an inert context so consumers never throw.
  if (!user && !authLoading) {
    const minimalValue = {
      ...EMPTY_SETTINGS,
      loading: false,
      loadSettings: () => Promise.resolve(),
      updateSystemSettings: () => Promise.reject(new Error('Not authenticated')),
      updateCompanyConfiguration: () => Promise.reject(new Error('Not authenticated')),
      updateConfigurableEnums: () => Promise.reject(new Error('Not authenticated')),
      refreshSettings: () => Promise.resolve(),
      getFormattedRoles: () => [],
      getFormattedDepartments: () => [],
      getFormattedSeverities: () => [],
      getFormattedStatuses: () => [],
      getSeverityColor: () => DEFAULT_LEVEL_COLOR,
      getStatusColor: () => '#757575',
      getPriorityColor: () => DEFAULT_LEVEL_COLOR
    };

    return (
      <SettingsContext.Provider value={minimalValue}>
        {children}
      </SettingsContext.Provider>
    );
  }

  const value = {
    ...settings,
    loadSettings,
    updateSystemSettings,
    updateCompanyConfiguration,
    updateConfigurableEnums,
    refreshSettings,
    getFormattedRoles,
    getFormattedDepartments,
    getFormattedSeverities,
    getFormattedStatuses,
    getSeverityColor,
    getStatusColor,
    getPriorityColor
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
